import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import useEmblaCarousel from 'embla-carousel-react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Section from '@/components/base/Section'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type ImageTextSlide = {
  image: WPImage
  title_variant: TypographyProps['variant']
  title_component: TypographyProps['component']
  title: string
  paragraph: string
}

export type ImageTextSlideProps = HTMLAttributes<HTMLDivElement> &
  Partial<ImageTextSlide>

const ImageTextSlide = ({
  image,
  title,
  title_variant,
  title_component,
  paragraph,
  className,
  ...props
}: ImageTextSlideProps) => {
  return (
    <Grid className={cn(['gap-y-[2rem]', className])} {...props}>
      <div className="col-span-4 md:order-2 md:col-span-3 lg:col-span-6">
        <div className="aspect-h-[260] aspect-w-[360] rounded-[3rem] bg-dark-blue md:aspect-h-[460] md:aspect-w-[645]">
          <Image
            source={image}
            fill
            fit="cover"
            className="w-full rounded-[3rem]"
          />
        </div>
      </div>

      <div className="col-span-4 md:order-1 md:col-span-3 lg:col-span-6">
        <div className="grid gap-y-[1rem] md:gap-y-[1.5rem]">
          {title ? (
            <Typography variant={title_variant} component={title_component}>
              {title}
            </Typography>
          ) : null}

          {paragraph ? (
            <Typography variant="body" paragraph>
              {paragraph}
            </Typography>
          ) : null}
        </div>
      </div>
    </Grid>
  )
}

export type ImageTextSliderProps = HTMLAttributes<HTMLElement> &
  Partial<{
    title: string
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    slides: ImageTextSlide[]
  }>

const ImageTextSlider = ({
  title,
  title_variant,
  title_component,
  slides = [],
  className,
  ...props
}: ImageTextSliderProps) => {
  const [ref, emblaApi] = useEmblaCarousel({ align: 'start' })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const needsCarousel = useMemo(() => slides?.length > 1, [slides?.length])

  const onInit = useCallback(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.reInit({ active: needsCarousel })
  }, [needsCarousel, emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onInit()
    onSelect()
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return (
    <Section className={cn([className])} {...props}>
      <Container>
        <Grid className="gap-y-[2rem]">
          {title ? (
            <div
              className={cn([
                'col-span-4 md:col-span-6 lg:col-span-12',
                'flex justify-end'
              ])}
            >
              <Typography
                variant={title_variant}
                component={title_component}
                className="grow text-heading"
              >
                {title}
              </Typography>

              {scrollSnaps.length > 1 ? (
                <div className="flex items-center space-x-[.3rem]">
                  {scrollSnaps.map((_, index) => (
                    <div key={index} className="flex shrink-0">
                      <button
                        className="-m-[.3rem] flex p-[.3rem]"
                        onClick={() => onDotButtonClick(index)}
                      >
                        <span className="sr-only">{`Slide ${index}`}</span>
                        <span
                          className={cn([
                            'flex h-[.8rem] w-[.8rem] rounded-full border-[.1rem] border-foreground',
                            'group-[.modal]:border-medium-blue',
                            {
                              'bg-foreground group-[.modal]:bg-medium-blue':
                                index === selectedIndex
                            }
                          ])}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {slides?.length > 0 ? (
            <div
              ref={ref}
              className={cn([
                'col-span-4 md:col-span-6 lg:col-span-12',
                '-mx-margin select-none overflow-hidden px-margin'
              ])}
            >
              <div className="-ml-margin flex touch-pan-y">
                {slides.map((slide, index) => (
                  <div
                    key={`image-card-${index}`}
                    className="min-w-0 shrink-0 grow-0 basis-full pl-margin"
                  >
                    <ImageTextSlide {...slide} />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Grid>
      </Container>
    </Section>
  )
}

export default ImageTextSlider
